import type { GlassVariant } from "../types/product";
import type { Cents } from "../types/money";
import { calculateGlassPrice } from "../pricing/engine";
import { PricingError } from "../pricing/errors";
import hingeLoads from "../data/shower-hinge-loads.json";
import hingeLoadsFeed from "../data/shower-hinge-loads.generated.json";

/**
 * Draagvermogen per scharnier-slug, uit twee bronnen.
 *
 * `shower-hinge-loads.json` is met de hand nagelopen en blijft leidend. De `.generated.json`
 * ernaast komt uit de Timmers-feed (`maximumLoad`/`maxDoorWidth`, of de omschrijving — zie
 * scripts/lib/scharnierbelasting.mjs) en vult alleen de gaten. Dat was nodig omdat de
 * configurator een scharnier zonder bekend draagvermogen stil laat vallen: 120 van de 374
 * scharnierregels waren zo onvindbaar, waaronder álle Storm glas-glas 180°. Op de 102 slugs die
 * in beide tabellen staan geven de feed en de handmatige lijst exact dezelfde waarden — vandaar
 * het vertrouwen om de rest uit de feed te halen.
 */
const HINGE_LOADS = { ...(hingeLoadsFeed as Record<string, { load?: number; maxW?: number }>), ...(hingeLoads as Record<string, { load?: number; maxW?: number }>) };

/** Standaard-scharnier (artikelslug) per montage — gebruikt als er (nog) geen keuze is. Bellagio
 *  50 kg / 1000 mm dekt élke maximale deur (1000×2300 ≈ 46 kg) veilig af. */
/**
 * Staat de douche-configurator open voor bestellingen?
 *
 * De 3D-module (`/douche/samenstellen` en `/douche/opstellingen`) is nog niet af: de opstellingen
 * kloppen nog niet goed genoeg om er een bindende order op te baseren. De pagina's blijven staan —
 * ze laten zien wat er komt — maar er kan niets besteld worden. "Installatieglas op maat" staat
 * hier los van en werkt gewoon.
 *
 * Eén schakelaar voor de site én de server: de knop uitzetten is niet genoeg, want een oude
 * (gecachte) bundel of een rechtstreekse aanroep zou er alsnog een order op kunnen zetten.
 * Zet 'm op `true` zodra de module af is.
 */
export const DOUCHE_BESTELBAAR = false;

export const SHOWER_DEFAULT_HINGE: Record<"wand" | "glas", string> = {
  wand: "bellagio-scharnier-wand-glas-90-glans-chroom",
  glas: "bellagio-scharnier-glas-glas-180-glans-chroom",
};

/**
 * Douche-opstellingen ("vaste wand + deur", à la Glasdiscount): parametrische recepten die een
 * complete opstelling samenstellen uit 8 mm gehard glas op maat (via de enkelglas-prijsengine,
 * inclusief scharnier-uitsparingen en knop-boring) plus écht Duco/Pauli-beslag uit de Timmers-
 * catalogus (Storm-serie: scharnieren, glasdeurknop, stabilisatiestang — in vier finishes).
 *
 * De module is puur: catalogus-toegang wordt geïnjecteerd via `ShowerCtx` zodat client en server
 * (createOrder = prijsautoriteit) exact dezelfde berekening draaien op hun eigen catalogus-kopie.
 */

// --- Finishes -----------------------------------------------------------------------------------
export type ShowerFinish = "chroom" | "mat-chroom" | "zwart" | "wit" | "rvs" | "gunmetal" | "goud" | "brons" | "koper";

export const SHOWER_FINISHES: { key: ShowerFinish; label: string; /** UI/3D-kleur */ hex: string }[] = [
  { key: "chroom", label: "Glans chroom", hex: "#c9cdd2" },
  { key: "mat-chroom", label: "Mat chroom", hex: "#b4b8bd" },
  { key: "zwart", label: "Mat zwart", hex: "#26282b" },
  // Mat wit bestond wél in de catalogus (12 scharnieruitvoeringen) maar niet als keuze, dus
  // werden die uitvoeringen stil weggelaten. Geen terugval: wit lijkt op niets anders in deze
  // rij, en een chromen scharnier in een witte set is geen "dichtstbijzijnde uitvoering".
  { key: "wit", label: "Mat wit", hex: "#eef0f2" },
  { key: "rvs", label: "RVS-look", hex: "#b8bcc0" },
  { key: "gunmetal", label: "Geborsteld gun metal", hex: "#5b5f66" },
  { key: "goud", label: "Geborsteld messing (goud)", hex: "#b99b5f" },
  { key: "brons", label: "Geborsteld brons", hex: "#8a6d48" },
  { key: "koper", label: "Mat koper", hex: "#b06b4a" },
];

/** De vier "standaard" finishes: chroom, mat chroom, mat zwart, RVS-look. Deze kleuren zijn tussen
 *  fabrikanten praktisch identiek, dus knop en scharnier mogen hier vrij gemengd worden. De overige
 *  ("speciale") finishes — gun metal, messing/goud, brons, koper — wijken per fabrikant af in tint;
 *  daar moet de knop van hetzelfde merk als het scharnier komen. Zie isSpecialShowerFinish. */
export const SHOWER_STANDARD_FINISHES: ShowerFinish[] = ["chroom", "mat-chroom", "zwart", "rvs"];

/** True voor een afwijkende (speciale) finish waarvan de tint per fabrikant verschilt. */
export function isSpecialShowerFinish(finish: ShowerFinish): boolean {
  return !SHOWER_STANDARD_FINISHES.includes(finish);
}

/** Nearest-first vervangketen als een onderdeel de gekozen finish niet heeft (knop/stang/profiel). */
const FINISH_FALLBACK: Record<ShowerFinish, ShowerFinish[]> = {
  chroom: [], "mat-chroom": ["chroom"], zwart: [], wit: [], rvs: ["gunmetal", "chroom"],
  gunmetal: ["chroom"], goud: [], brons: ["goud", "chroom"], koper: ["goud", "chroom"],
};

// --- Opstellingen -------------------------------------------------------------------------------
export type ShowerLayout =
  | "nis-deur" | "deur-zijpaneel" | "nis-dubbele-deur" | "nis-deur-dubbel-zijlicht"
  | "hoekopstelling" | "hoek-deur-dubbel-zijlicht"
  | "inloop-enkel" | "inloop-dubbel";

/** Hoofdtype (stap 1 in de configurator) — elk hoofdtype heeft meerdere subopstellingen. */
export type ShowerFamily = "nisdeur" | "hoekopstelling" | "inloopdouche";

export const SHOWER_FAMILIES: { key: ShowerFamily; label: string; description: string }[] = [
  { key: "nisdeur", label: "Nisdeur", description: "Tussen twee muren — deur(en) en eventueel vaste zijlichten in lijn." },
  { key: "hoekopstelling", label: "Hoekopstelling", description: "Twee haakse zijden in een hoek — deur(en) met vaste wand of zijlichten." },
  { key: "inloopdouche", label: "Inloopdouche", description: "Open instap met één of twee vaste glaswanden — geen deur." },
];

export interface ShowerPanelSpec {
  key: string;
  label: string;
  /** Deur (scharnieren + knop + boringen) of vast glasdeel (muurprofiel + stang). */
  kind: "deur" | "vast";
  /** Vlak in de opstelling: "front" = naast elkaar vooraan, "zijkant" = haaks terug naar de muur. */
  plane: "front" | "zijkant";
  minWmm: number;
  maxWmm: number;
  defaultWmm: number;
}

export interface ShowerLayoutSpec {
  key: ShowerLayout;
  family: ShowerFamily;
  label: string;
  description: string;
  panels: ShowerPanelSpec[];
  /** Waar de deur(en) scharnieren: aan de muur (glas-wand) of aan een zijlicht (glas-glas). */
  hingeMount: "wand" | "glas";
  /** Haakse hoek tussen twee glasdelen → hoek-afdichtingsprofiel. */
  corner?: boolean;
}

/** Hoogte geldt voor de hele opstelling (alle glasdelen even hoog). Ruim ingesteld: de échte
 *  grens is of er nog een scharnier is dat het glasgewicht draagt (max ± 70 kg), niet een vaste maat. */
export const SHOWER_MIN_H_MM = 1600;
export const SHOWER_MAX_H_MM = 3000;
export const SHOWER_DEFAULT_H_MM = 2000;

// Minimum-breedtes respecteren de 1:10-verhouding van gehard glas (hardingsoven):
// bij de maximale hoogte (2300 mm) moet de korte zijde ≥ 230 mm zijn.
const DEUR: ShowerPanelSpec = { key: "deur", label: "Deur", kind: "deur", plane: "front", minWmm: 450, maxWmm: 1200, defaultWmm: 900 };
const DEUR2: ShowerPanelSpec = { ...DEUR, key: "deur2", label: "Deur rechts" };
// Vaste panelen (zijlicht/wand) hebben geen scharnieren → veel ruimere max-breedte dan de deur.
const ZIJLICHT: ShowerPanelSpec = { key: "zijlicht", label: "Zijlicht", kind: "vast", plane: "front", minWmm: 230, maxWmm: 2000, defaultWmm: 400 };
const ZIJLICHT2: ShowerPanelSpec = { ...ZIJLICHT, key: "zijlicht2", label: "Zijlicht rechts" };
const ZIJLICHT_ZIJ: ShowerPanelSpec = { ...ZIJLICHT, key: "zijlicht2", label: "Zijlicht (haakse zijde)", plane: "zijkant", defaultWmm: 900 };
const WAND: ShowerPanelSpec = { key: "wand", label: "Vaste wand", kind: "vast", plane: "zijkant", minWmm: 300, maxWmm: 2000, defaultWmm: 900 };
const WAND_FRONT: ShowerPanelSpec = { ...WAND, key: "wand", label: "Glaswand", plane: "front", defaultWmm: 1500 };
// Inloop-dubbel = twee losstaande, haaks op de achterwand geplaatste glaswanden met een doorgang
//   ertussen. Beide "zijkant"; de breedte is hoe ver de wand van de achterwand uitsteekt.
const INLOOP_WAND1: ShowerPanelSpec = { key: "wand", label: "Glaswand 1", kind: "vast", plane: "zijkant", minWmm: 300, maxWmm: 2000, defaultWmm: 900 };
const INLOOP_WAND2: ShowerPanelSpec = { key: "wand2", label: "Glaswand 2", kind: "vast", plane: "zijkant", minWmm: 300, maxWmm: 2000, defaultWmm: 900 };

export const SHOWER_LAYOUTS: ShowerLayoutSpec[] = [
  // — Nisdeur (tussen twee muren, alles in lijn) —
  {
    key: "nis-deur",
    family: "nisdeur",
    label: "Enkele douchedeur",
    description: "Eén draaideur tussen twee muren — scharniert aan de muur.",
    panels: [DEUR],
    hingeMount: "wand",
  },
  {
    // Bestond al als "Deur + zijpaneel" — zelfde recept, nu als nis-suboptie (key blijft
    // ongewijzigd zodat bestaande winkelwagens/bestellingen geldig blijven).
    key: "deur-zijpaneel",
    family: "nisdeur",
    label: "Enkele douchedeur met zijlicht",
    description: "Vast zijlicht + draaideur in lijn — de deur scharniert aan het zijlicht.",
    panels: [ZIJLICHT, DEUR],
    hingeMount: "glas",
  },
  {
    key: "nis-dubbele-deur",
    family: "nisdeur",
    label: "Dubbele douchedeur",
    description: "Twee draaideuren die in het midden sluiten — elk scharniert aan een muur.",
    panels: [DEUR, DEUR2],
    hingeMount: "wand",
  },
  {
    key: "nis-deur-dubbel-zijlicht",
    family: "nisdeur",
    label: "Enkele douchedeur met dubbel zijlicht",
    description: "Deur in het midden met een vast zijlicht aan weerszijden.",
    panels: [ZIJLICHT, DEUR, ZIJLICHT2],
    hingeMount: "glas",
  },
  // — Hoekopstelling (twee haakse zijden) —
  {
    key: "hoekopstelling",
    family: "hoekopstelling",
    label: "Vaste wand en deur",
    description: "Draaideur met een haakse vaste wand — de deur scharniert aan de muur.",
    panels: [DEUR, WAND],
    hingeMount: "wand",
    corner: true,
  },
  {
    key: "hoek-deur-dubbel-zijlicht",
    family: "hoekopstelling",
    label: "Enkele douchedeur met dubbel zijlicht",
    description: "Zijlicht + deur vooraan, haaks zijlicht opzij — de deur scharniert aan het zijlicht.",
    panels: [ZIJLICHT, DEUR, ZIJLICHT_ZIJ],
    hingeMount: "glas",
    corner: true,
  },
  // — Inloopdouche (open instap, geen deur) —
  {
    key: "inloop-enkel",
    family: "inloopdouche",
    label: "Enkele wand",
    description: "Eén vaste glaswand met open instap.",
    panels: [WAND_FRONT],
    hingeMount: "wand",
  },
  {
    key: "inloop-dubbel",
    family: "inloopdouche",
    label: "Dubbele wand",
    description: "Twee losstaande glaswanden met een open doorgang ertussen.",
    panels: [INLOOP_WAND1, INLOOP_WAND2],
    hingeMount: "wand",
    corner: true,
  },
];

export function getShowerLayout(key: string): ShowerLayoutSpec | undefined {
  return SHOWER_LAYOUTS.find((l) => l.key === key);
}

/** Subopstellingen van een hoofdtype, in menu-volgorde. */
export function showerLayoutsForFamily(family: ShowerFamily): ShowerLayoutSpec[] {
  return SHOWER_LAYOUTS.filter((l) => l.family === family);
}

/** Effectieve scharniermontage voor de (eerste) deur: hangt bij sommige opstellingen af van de
 *  gekozen zijde — links een vast glasdeel = glas-glas, een muur = wand-glas. Zonder deur of bij
 *  meerdere deuren houdt de opstelling z'n eigen montage aan. */
export function showerHingeMount(layoutKey: string, hingeSide: "links" | "rechts" = "links"): "wand" | "glas" {
  const layout = getShowerLayout(layoutKey);
  if (!layout) return "wand";
  const doorCount = layout.panels.filter((p) => p.kind === "deur").length;
  if (doorCount !== 1) return layout.hingeMount;
  const front = layout.panels.filter((p) => p.plane === "front");
  const doorIdx = front.findIndex((p) => p.kind === "deur");
  const neighbor = hingeSide === "links" ? front[doorIdx - 1] : front[doorIdx + 1];
  if (neighbor) return neighbor.kind === "vast" ? "glas" : "wand";
  // Geen front-buurpaneel: bij een haakse opstelling grenst de rechterzijde aan het haakse glasdeel.
  if (layout.corner && hingeSide === "rechts" && layout.panels.some((p) => p.plane === "zijkant" && p.kind === "vast")) return "glas";
  return "wand";
}

// --- Beslag-recepten (échte Timmers-artikel-slugs, Storm-serie per finish) -----------------------
type HardwareRole = "scharnier" | "knop" | "stabilisatiestang" | "stang-glasaansluiting" | "stang-wandaansluiting" | "wandprofiel" | "wandklem" | "afdichting-onder" | "afdichting-hoek";

export interface HardwarePick {
  slug: string;
  /** true = dit onderdeel bestaat niet in de gekozen finish; dichtstbijzijnde variant gekozen. */
  fallback?: boolean;
}

/** Beslag-rollen die de klant zelf kan samenstellen. */
export type ShowerHwRole = "scharnier" | "knop" | "stang" | "stangGlas" | "stangWand" | "uProfiel" | "klem" | "sealDoor" | "sealHoek";

/** Per finish een artikel — alléén de finishes die dit onderdeel écht heeft (rest ontbreekt). */
type FinishMap = Partial<Record<ShowerFinish, HardwarePick>>;
/** Zelfde artikel voor elke finish (finish-onafhankelijke onderdelen, bv. afdichtprofielen). */
const uni = (slug: string): FinishMap => Object.fromEntries(SHOWER_FINISHES.map((f) => [f.key, { slug }])) as FinishMap;

/** Kies het artikel voor een finish; valt terug op de dichtstbijzijnde als die finish ontbreekt. */
function pickForFinish(map: FinishMap | undefined, finish: ShowerFinish): HardwarePick | undefined {
  if (!map) return undefined;
  if (map[finish]) return map[finish];
  for (const alt of FINISH_FALLBACK[finish]) if (map[alt]) return { ...map[alt]!, fallback: true };
  // Laatste redmiddel: welke finish dan ook.
  const any = SHOWER_FINISHES.map((f) => map[f.key]).find(Boolean);
  return any ? { ...any, fallback: true } : undefined;
}

/** De finishes die een scharnier-optie écht heeft (wand of glas, afhankelijk van de montage). */
export function showerScharnierFinishes(id: string | undefined, hingeMount: "wand" | "glas"): ShowerFinish[] {
  const opt = showerHwOption("scharnier", id);
  const map = hingeMount === "glas" ? (opt.glas ?? opt.wand) : (opt.wand ?? opt.glas);
  return SHOWER_FINISHES.map((f) => f.key).filter((k) => map?.[k]);
}

/** Finishes die de vaste-paneel-onderdelen (stang + muurprofiel) delen — voor deurloze opstellingen. */
export function showerPanelFinishes(): ShowerFinish[] {
  const stang = SHOWER_HW_OPTIONS.stang[0]!.picks;
  return SHOWER_FINISHES.map((f) => f.key).filter((k) => stang?.[k]);
}

/** Resolvet het artikel (slug + fallback-vlag) van een beslag-optie voor een finish — voor de UI-kaartjes. */
export function showerHwResolve(opt: ShowerHwOption, hingeMount: "wand" | "glas", finish: ShowerFinish): HardwarePick | undefined {
  const map = opt.picks ?? (hingeMount === "glas" ? (opt.glas ?? opt.wand) : (opt.wand ?? opt.glas));
  return pickForFinish(map, finish);
}

export interface ShowerHwOption {
  id: string;
  label: string;
  /** Korte omschrijving voor de keuzekaart. */
  sub?: string;
  /** Scharnier-opties: aparte artikel-sets voor wand- en glas-montage. */
  wand?: FinishMap;
  glas?: FinishMap;
  /** Overige rollen: één artikel-set per finish. */
  picks?: FinishMap;
  /** Scharnier-opties: max. deurgewicht (kg) dat een PAAR (2 stuks) draagt. Zwaardere deuren
   *  krijgen 3 scharnieren (≈ +50% draagvermogen); daarboven niet leverbaar. */
  maxKgPerPair?: number;
}

// --- Glasgewicht + aantal scharnieren -----------------------------------------------------------
/** Standaard max. deurgewicht (kg) per paar scharnieren als een serie geen eigen waarde opgeeft. */
export const SHOWER_HINGE_DEFAULT_MAX_KG = 40;
/** Floatglas ≈ 2,5 kg/m² per mm dikte. */
const GLASS_KG_PER_M2_PER_MM = 2.5;

/** Gewicht (kg) van één glasplaat op maat. */
export function showerGlassWeightKg(widthMm: number, heightMm: number, thicknessMm: number = SHOWER_DOOR_GLASS.thicknessMm): number {
  const areaM2 = (widthMm / 1000) * (heightMm / 1000);
  return Math.round(areaM2 * thicknessMm * GLASS_KG_PER_M2_PER_MM * 10) / 10;
}

/** Aantal wandklemmen per vast paneel als de klant voor klemmen kiest in plaats van een profiel. */
export const SHOWER_KLEMMEN_PER_PANEEL = 2;

/** Boven deze deurhoogte (mm) is een DERDE scharnier nodig — voor de STABILITEIT, niet het gewicht. */
export const SHOWER_THIRD_HINGE_ABOVE_MM = 2000;
/** Aantal scharnieren per deur: 3 bij deuren hoger dan 2 m (stabiliteit), anders 2. */
export function showerHingeCount(doorHeightMm: number): 2 | 3 {
  return doorHeightMm > SHOWER_THIRD_HINGE_ABOVE_MM ? 3 : 2;
}

/** Draagvermogen (kg/paar) + max. deurbreedte (mm) van een scharnier-ARTIKEL uit de Timmers-productinfo.
 *  `fallbackKg` = terugval als de belasting niet vermeld is (bv. Storm heeft geen waarde). */
export function showerHingeCapacity(slug: string | undefined, fallbackKg = SHOWER_HINGE_DEFAULT_MAX_KG): { maxKgPair: number; maxDoorWidthMm: number | null; fromCatalog: boolean } {
  const rec = slug ? HINGE_LOADS[slug] : undefined;
  return { maxKgPair: rec?.load ?? fallbackKg, maxDoorWidthMm: rec?.maxW ?? null, fromCatalog: !!rec?.load };
}

/** Geschiktheid van een scharnier voor een deur: draagvermogen (kg/paar, harde max — een 3e
 *  scharnier verhoogt de capaciteit NIET) + max. deurbreedte. Het AANTAL (2/3) volgt de hoogte. */
export function showerHingeFit(
  doorWeightKg: number,
  doorWidthMm: number,
  doorHeightMm: number,
  cap: { maxKgPair: number; maxDoorWidthMm: number | null },
): { count: 2 | 3 | null; reason?: "gewicht" | "breedte" } {
  if (cap.maxDoorWidthMm != null && doorWidthMm > cap.maxDoorWidthMm) return { count: null, reason: "breedte" };
  if (doorWeightKg > cap.maxKgPair) return { count: null, reason: "gewicht" };
  return { count: showerHingeCount(doorHeightMm) };
}

/** Kiesbare beslag-opties per rol — allemaal échte catalogus-artikelen (geverifieerde slugs).
 *  De eerste optie per rol is de standaard. `fallback: true` = niet leverbaar in die kleur;
 *  dichtstbijzijnde uitvoering wordt geleverd. */
export const SHOWER_HW_OPTIONS: Record<ShowerHwRole, ShowerHwOption[]> = {
  scharnier: [
    {
      id: "storm",
      label: "Storm",
      sub: "rond — chroom, zwart, gun metal, messing, brons, koper",
      maxKgPerPair: 40,
      wand: {
        chroom: { slug: "douchescharnier-storm-glas-wand-90-glans-chroom" },
        zwart: { slug: "douchescharnier-storm-glas-wand-90-mat-zwart" },
        gunmetal: { slug: "douchescharnier-storm-glas-wand-90-geborsteld-gun-metal-pvd" },
        goud: { slug: "douchescharnier-storm-glas-wand-90-geborsteld-mat-messing-pvd" },
        brons: { slug: "douchescharnier-storm-glas-wand-90-geborsteld-brons-messing-pvd" },
        koper: { slug: "douchescharnier-storm-glas-wand-90-mat-koper-pvd" },
      },
      glas: {
        chroom: { slug: "douchescharnier-storm-glas-glas-180-glans-chroom" },
        zwart: { slug: "douchescharnier-storm-glas-glas-180-mat-zwart" },
        gunmetal: { slug: "douchescharnier-storm-glas-glas-180-geborsteld-gun-metal-pvd" },
        goud: { slug: "douchescharnier-storm-glas-glas-180-geborsteld-mat-messing-pvd" },
      },
    },
    {
      id: "marine",
      label: "Marine",
      sub: "klassiek afgerond — chroom, mat chroom, rvs",
      maxKgPerPair: 40,
      wand: {
        chroom: { slug: "douchescharnier-marine-wand-glas-glans-chroom" },
        "mat-chroom": { slug: "douchescharnier-marine-wand-glas-mat-chroom" },
        rvs: { slug: "douchescharnier-marine-wand-glas-rvs-effect" },
      },
      glas: {
        chroom: { slug: "douchescharnier-marine-glas-glas-glans-chroom-0132620R" },
        "mat-chroom": { slug: "douchescharnier-marine-glas-glas-mat-chroom-0132620r-31" },
      },
    },
    {
      id: "fardello",
      label: "Fardello",
      sub: "zwaar (42 kg) — chroom, mat zwart, rvs",
      maxKgPerPair: 42,
      wand: {
        chroom: { slug: "douchescharnier-fardello-glaswand-glans-chroom-42kg" },
        zwart: { slug: "douchescharnier-fardello-glaswand-mat-zwart-42kg" },
        rvs: { slug: "douchescharnier-fardello-glaswand-rvs-look-42kg" },
      },
      glas: {
        chroom: { slug: "douchescharnier-fardello-glasglas-glans-chroom-42kg" },
        "mat-chroom": { slug: "douchescharnier-fardello-glasglas-mat-chroom-42kg" },
        rvs: { slug: "douchescharnier-fardello-glasglas-rvs-look-42kg" },
      },
    },
    {
      id: "flamea",
      label: "Flamea+",
      sub: "compact — chroom, mat chroom, zwart, goud, rvs",
      maxKgPerPair: 40,
      wand: {
        chroom: { slug: "flamea-scharnier-wandglas-90-enkele-schoefplaat-incl-afdekkapjes-glans-chroom" },
        "mat-chroom": { slug: "flamea-sauna-scharnier-wand-glas-90-mat-chroom" },
        zwart: { slug: "flamea-sauna-scharnier-wand-glas-90-mat-zwart" },
        goud: { slug: "flamea-sauna-scharnier-wand-glas-90-glans-goud" },
        rvs: { slug: "flamea-sauna-scharnier-wand-glas-90-rvs-effect" },
      },
    },
  ],
  knop: [
    {
      id: "storm-knop",
      label: "Storm knop ø32",
      sub: "dubbelzijdig, in alle kleuren",
      picks: {
        chroom: { slug: "glasdeurknop-storm-32x32-dubbelzijdig-glans-chroom" },
        zwart: { slug: "glasdeurknop-storm-32x32-dubbelzijdig-mat-zwart" },
        gunmetal: { slug: "glasdeurknop-storm-32x32-dubbelzijdig-geborsteld-gun-metal-pvd" },
        goud: { slug: "glasdeurknop-storm-32x32-dubbelzijdig-geborsteld-mat-messing-pvd" },
      },
    },
    {
      id: "grijpholte",
      label: "Knop met grijpholte",
      sub: "komgreep, per paar",
      picks: {
        chroom: { slug: "deurknop-paar-met-grijpholte-glans-chroom-zonder-pauli-logo" },
        zwart: { slug: "deurknop-paar-met-grijpholte-mat-zwart-zonder-pauli-logo" },
        gunmetal: { slug: "deurknop-paar-met-grijpholte-mat-chroom-flamea-wg-8278-31", fallback: true },
        goud: { slug: "deurknop-paar-met-grijpholte-goud-glans-zonder-pauli-logo" },
      },
    },
  ],
  // Stabilisatiestang = 3 losse delen: de stang zelf + een glasaansluiting + een wandaansluiting.
  stang: [
    {
      id: "rechthoekig-1000",
      label: "Rechthoekige stang 1000 mm",
      sub: "19×19 mm, inkortbaar",
      picks: {
        chroom: { slug: "rechthoekige-stabilisatie-stang-19x19x1000mm-glans-chroom" },
        rvs: { slug: "rechthoekige-stabilisatie-stang-19x19x1000mm-rvs-look" },
        zwart: { slug: "rechthoekige-stabilisatie-stang-19x19x1000mm-mat-zwart" },
        gunmetal: { slug: "rechthoekige-stabilisatie-stang-19x19x1000mm-glans-chroom", fallback: true },
        goud: { slug: "rechthoekige-stabilisatie-stang-19x19x1000mm-glans-chroom", fallback: true },
      },
    },
  ],
  stangGlas: [
    {
      id: "glas-recht",
      label: "Glasbevestiging",
      sub: "klemt op het glas, 19×19 mm",
      picks: {
        chroom: { slug: "glasbevestiging-rechthoekige-stabilisatie-stang-voor-19x19mm-buis-glans-chroom" },
        rvs: { slug: "glasbevestiging-draaibaar-open-rechthoekige-stabilisatiestang-voor-19x19mm-buis-rvs-look" },
        zwart: { slug: "glasbevestiging-rechthoekige-stabilisatie-stang-voor-19x19mm-buis-mat-zwart" },
        gunmetal: { slug: "glasbevestiging-rechthoekige-stabilisatie-stang-voor-19x19mm-buis-glans-chroom", fallback: true },
        goud: { slug: "glasbevestiging-rechthoekige-stabilisatie-stang-voor-19x19mm-buis-glans-chroom", fallback: true },
      },
    },
  ],
  stangWand: [
    {
      id: "wand-recht",
      label: "Wand-/plafondbevestiging",
      sub: "aan de muur of het plafond, 19×19 mm",
      picks: {
        chroom: { slug: "wand-plafond-bevestiging-rechthoekige-stabilisatiestang-voor-19x19mm-buis-glans-chroom" },
        rvs: { slug: "wand-plafondbevestiging-rechthoekige-stabilisatiestang-voor-19x19mm-buis-rvs-look" },
        zwart: { slug: "wand-plafond-bevestiging-rechthoekige-stabilisatiestang-voor-19x19mm-buis-glans-chroom", fallback: true },
        gunmetal: { slug: "wand-plafond-bevestiging-rechthoekige-stabilisatiestang-voor-19x19mm-buis-glans-chroom", fallback: true },
        goud: { slug: "wand-plafond-bevestiging-rechthoekige-stabilisatiestang-voor-19x19mm-buis-glans-chroom", fallback: true },
      },
    },
  ],
  uProfiel: [
    {
      id: "dry-glaze",
      label: "Dry glaze profiel",
      sub: "kliksysteem zonder kit, 2400 mm",
      picks: {
        chroom: { slug: "dry-glaze-u-profiel-2400mm-glans-chroom" },
        zwart: { slug: "dry-glaze-u-profiel-2400mm-mat-zwart" },
        gunmetal: { slug: "dry-glaze-u-profiel-2400mm-mat-zwart", fallback: true },
        goud: { slug: "dry-glaze-u-profiel-2400mm-glans-chroom", fallback: true },
      },
    },
    {
      id: "estlegno",
      label: "Estlegno U-profiel",
      sub: "aluminium 15×15 mm",
      picks: {
        chroom: { slug: "02243u15-30-estlegno-u-profiel-15x15x15mm-aluminium-gepolijst" },
        zwart: { slug: "02243u15-30-estlegno-u-profiel-15x15x15mm-aluminium-gepolijst", fallback: true },
        gunmetal: { slug: "02243u15-35-estlegno-u-profiel-RVS-look-15x15x15mm", fallback: true },
        goud: { slug: "02243u15-06-estlegno-u-profiel-15x15x15mm-mat-messing" },
      },
    },
  ],
  /**
   * Wandklemmen in plaats van een doorlopend muurprofiel. Vakmensen vragen hier zelf om — het is
   * de nettere, minder zichtbare bevestiging — en tot 20-10-2026 kon je die keuze hier niet maken.
   * De Bellagio-serie is de standaard omdat die álle acht uitvoeringen heeft, brons inbegrepen.
   * Twee klemmen per vast paneel; dat is wat er bij een klemmenset hoort en wat klanten opgeven.
   */
  klem: [
    {
      id: "bellagio-klem",
      label: "Bellagio wandklem",
      sub: "glas-wand 90°, 2 per paneel",
      picks: {
        chroom: { slug: "bellagio-hoekverbinder-glas-wand-90-glans-chroom" },
        "mat-chroom": { slug: "bellagio-hoekverbinder-glas-wand-90-glans-chroom", fallback: true },
        zwart: { slug: "bellagio-hoekverbinder-glas-wand-90-mat-zwart" },
        wit: { slug: "bellagio-hoekverbinder-glas-wand-90-mat-wit" },
        rvs: { slug: "bellagio-hoekverbinder-glas-wand-90-rvs-look" },
        gunmetal: { slug: "bellagio-hoekverbinder-glas-wand-90-brushed-gun-metal" },
        goud: { slug: "bellagio-hoekverbinder-glas-wand-90-glans-messing" },
        brons: { slug: "bellagio-hoekverbinder-glas-wand-90-brushed-bronze" },
        koper: { slug: "bellagio-hoekverbinder-glas-wand-90-matt-brushed-copper" },
      },
    },
    {
      id: "slobgaten",
      label: "Glasklem met slobgaten",
      sub: "glas-wand 90°, in hoogte verstelbaar",
      picks: {
        chroom: { slug: "glasklem-glas-wand-90-montage-plaat-voorzien-van-slobgaten-glans-chroom" },
        zwart: { slug: "glasklem-glas-wand-90-montage-plaat-voorzien-van-slobgaten-diep-mat-zwart" },
        rvs: { slug: "glasklem-glas-wand-90-montage-plaat-voorzien-van-slobgaten-rvs-effect" },
      },
    },
  ],
  sealDoor: [
    { id: "transparant", label: "Transparant", sub: "onderzijde deur, 2010 mm", picks: uni("afdichtprofiel-voor-6-8mm-glas-lengte-2010mm-voor-onder-aan-de-deur") },
    { id: "zwart", label: "Zwart", sub: "onderzijde deur, 2010 mm", picks: uni("8864ku3-8k-2010-afdichtprofiel-voor-8mm-glas-lengte-2010mm-voor-onder-aan-de-deur-zwart") },
  ],
  sealHoek: [
    { id: "transparant", label: "Transparant", sub: "hoek, 3 flappen, 2200 mm", picks: uni("afdichtprofiel-hoek-3-flappen-d8mm-l2200mm") },
    { id: "zwart", label: "Zwart", sub: "hoek, 3 flappen, 2200 mm", picks: uni("afdichtprofiel-hoek-3-flappen-d8mm-l2200mm-zwart") },
  ],
};

/** Gekozen optie voor een rol (onbekende/lege keuze → standaard-optie). */
export function showerHwOption(role: ShowerHwRole, id?: string): ShowerHwOption {
  const opts = SHOWER_HW_OPTIONS[role];
  return opts.find((o) => o.id === id) ?? opts[0]!;
}

/** Glassoort per paneeltype: DEUREN = installatieglas, VASTE panelen = veiligheidsglas (gehard).
 *  Beide 8 mm. Gedeeld door de client- en server-shims. */
export const SHOWER_DOOR_GLASS = { productSlug: "installatieglas", subcategory: "Installatieglas", thicknessMm: 8 } as const;
// Gehard glas zit sinds 12-10-2026 onder het product "veiligheidsglas": Timmers levert het nu ook
// via de API en die is leidend; onze prijslijst vult alleen de ontbrekende diktes aan (helder
// bestaat in de API alleen in 4 en 5 mm). Beide bronnen vallen daar in één rubriek samen, dus ook
// de paneelkleuren hieronder dragen de API-naamgeving.
export const SHOWER_PANEL_GLASS = { productSlug: "veiligheidsglas", subcategory: "Gehard glas", thicknessMm: 8 } as const;
/** Timmers rekent voor glas dat in een douche gaat (zowel deuren als vaste panelen) de aparte
 *  dealerkorting SHOWER_DOORS_PANELS (55%), niet de generieke gehard-korting (57%). We herschalen
 *  daarom de glas-m²-prijs naar deze korting: deur (installatieglas, al 55%) blijft gelijk, het
 *  gehard paneel (57%) wordt naar 55% gecorrigeerd. (Bron: data/timmers/discounts.json.) */
export const SHOWER_GLASS_DISCOUNT_PCT = 55;
/** Kiesbare glasdiktes (mm) voor de hele cabine — beide glassoorten bestaan in 8 én 10 mm. */
export const SHOWER_GLASS_THICKNESSES = [8, 10] as const;
/** Default-glasdikte als de invoer er geen meegeeft (mm). */
export const SHOWER_DEFAULT_THICKNESS_MM = 8;

/** Een gedeelde glas-uitstraling voor de hele cabine; elke uitstraling resolvet in BEIDE
 *  glassoorten (deur = installatieglas-kleur, vast paneel = gehard-kleur), zodat de cabine
 *  uniform oogt maar per paneel correct wordt geprijsd. */
export interface ShowerGlassAppearance {
  key: string;
  label: string;
  group: "helder" | "mat" | "gekleurd";
  /** Kleurnaam in de installatieglas-catalogus (deuren). */
  doorColor: string;
  /** Kleurnaam in de gehard-catalogus (vaste panelen). */
  panelColor: string;
}
export const SHOWER_GLASS_APPEARANCES: ShowerGlassAppearance[] = [
  { key: "helder", label: "Helder", group: "helder", doorColor: "Helder", panelColor: "Gehard blank" },
  { key: "extra-helder", label: "Extra helder", group: "helder", doorColor: "Extra helder", panelColor: "Gehard extra helder" },
  { key: "mat", label: "Mat (acide)", group: "mat", doorColor: "Acide blank", panelColor: "Gehard acide" },
  { key: "mat-extra-helder", label: "Mat extra helder", group: "mat", doorColor: "Acide extra helder", panelColor: "Gehard acide extra helder" },
  // Deurkleur = de kleurnaam zoals hij in de installatieglas-DATA staat ("Brons"), niet de
  // rijnaam uit de Timmers-PDF ("Brons-grijs-groen"). Die stond er eerst: de deur vond dan geen
  // glas en viel terug op helder — bronzen deuren gingen weg voor de prijs van helder glas.
  // "Blauw" is weg sinds de prijslijst van 13-07-2026 (tot einde voorraad, gehard én installatie).
  // Een kleur die hier staat MOET in beide soorten bestaan: shower-kleuren.guard.test.ts.
  { key: "brons", label: "Brons", group: "gekleurd", doorColor: "Brons", panelColor: "Gehard brons" },
  { key: "dark-grey", label: "Dark grey", group: "gekleurd", doorColor: "Dark grey", panelColor: "Gehard dark grey" },
];
export const SHOWER_DEFAULT_APPEARANCE = "helder";
/** De uitstraling waarop alles terugvalt als de gekozen kleur er niet is — één bron voor client,
 *  server en engine, zodat een kleurnaam-wijziging niet op drie plekken nagelopen hoeft te worden. */
export const STANDAARD_UITSTRALING: ShowerGlassAppearance =
  SHOWER_GLASS_APPEARANCES.find((a) => a.key === SHOWER_DEFAULT_APPEARANCE) ?? SHOWER_GLASS_APPEARANCES[0]!;

/** Zoek een uitstraling op (tolerant: op key, label of oude kleurnaam) — anders helder. */
export function showerAppearance(key?: string): ShowerGlassAppearance {
  const k = (key ?? "").trim().toLowerCase();
  return (
    SHOWER_GLASS_APPEARANCES.find((a) => a.key === k) ??
    SHOWER_GLASS_APPEARANCES.find((a) => a.label.toLowerCase() === k) ??
    SHOWER_GLASS_APPEARANCES.find((a) => a.doorColor.toLowerCase() === k || a.panelColor.toLowerCase() === k) ??
    SHOWER_GLASS_APPEARANCES[0]!
  );
}

/** Alle artikel-slugs die de recepten kunnen gebruiken — voor catalogus-validatie in tests. */
export function showerHardwareSlugs(): string[] {
  const out = new Set<string>();
  for (const opts of Object.values(SHOWER_HW_OPTIONS)) {
    for (const opt of opts) {
      for (const map of [opt.wand, opt.glas, opt.picks]) {
        if (map) for (const pick of Object.values(map)) out.add(pick.slug);
      }
    }
  }
  return [...out];
}

// --- Glas-bewerkingen per rol -------------------------------------------------------------------
/** Scharnier-uitsparing (Storm): rechthoekige uitsparing, langste zijde ± 50 mm. */
const HINGE_CUTOUT_MM = 50;
/** Boorgat voor de Storm-glasdeurknop (doorgaand, ø14). */
const KNOB_BORE_MM = 14;

// --- Invoer / uitvoer ---------------------------------------------------------------------------
export interface ShowerInput {
  layout: ShowerLayout;
  finish: ShowerFinish;
  heightMm: number;
  /** Breedte per paneel, op volgorde van `SHOWER_LAYOUTS[].panels` (deur eerst). */
  widthsMm: number[];
  /** Matglas (gezuurd/acide) i.p.v. helder — legacy; gebruik bij voorkeur glassColor. */
  matGlass?: boolean;
  /** Glas-uitstraling: key uit SHOWER_GLASS_APPEARANCES (bv. "helder", "mat", "brons").
   *  Bepaalt per paneel de kleur in installatieglas (deur) én gehard (vast). Default "helder".
   *  (Oude waarden = kleurnaam worden tolerant herkend.) */
  glassColor?: string;
  /** Glasdikte (mm) voor deur (installatieglas) én vaste panelen (gehard) — 8 of 10. Default 8.
   *  10 mm is steviger (en ~25% zwaarder → strengere scharnierkeuze) tegen een hogere glasprijs. */
  glassThicknessMm?: number;
  /** Zelf samengesteld beslag: gekozen optie-id per rol (leeg = standaard per rol). */
  hardware?: Partial<Record<ShowerHwRole, string>>;
  /** Scharnierzijde van de (eerste) deur — links of rechts (default links). Bepaalt de oriëntatie
   *  én, bij sommige opstellingen, of de deur aan glas of aan de muur scharniert. */
  hingeSide?: "links" | "rechts";
  /** Afdichtprofiel onder de deur meebestellen (default false — je kunt ook afkitten). */
  sealDoorOn?: boolean;
  /** Hoekafdichtprofiel meebestellen (default false — je kunt ook afkitten). */
  sealHoekOn?: boolean;
  /** Montage van de stabilisatiestang: recht (haaks), schuin (45°) of T-stang (T-stuk). */
  stangType?: "recht" | "schuin" | "t-stuk";
  /** Hoe een vast paneel aan de wand komt: doorlopend muurprofiel (default) of twee wandklemmen
   *  per paneel. Het is een keuze, geen extra: klemmen vervangen het profiel. */
  paneelBevestiging?: "profiel" | "klemmen";
  /** Cabinediepte in mm (alleen spec — bepaalt o.a. de in te korten stabilisatiestang).
   *  Alleen relevant bij opstellingen zónder haaks glasdeel; daar is de diepte het glas zelf. */
  depthMm?: number;
  quantity: number;
}

export interface ShowerGlassLine {
  panel: ShowerPanelSpec["key"];
  label: string;
  widthMm: number;
  heightMm: number;
  /** Glasprijs per paneel incl. bewerkingen (excl. btw, cents). */
  cents: Cents;
}

export interface ShowerHardwareLine {
  role: HardwareRole;
  slug: string;
  label: string;
  count: number;
  unitCents: Cents;
  totalCents: Cents;
  /** Onderdeel niet leverbaar in de gekozen finish → dichtstbijzijnde variant gekozen. */
  finishFallback?: boolean;
}

export interface ShowerPrice {
  layout: ShowerLayoutSpec;
  finish: ShowerFinish;
  glass: ShowerGlassLine[];
  hardware: ShowerHardwareLine[];
  glassCents: Cents;
  hardwareCents: Cents;
  /** Prijs per opstelling (glas + beslag), excl. btw. */
  unitCents: Cents;
  totalCents: Cents;
}

export class ShowerPricingError extends Error {
  readonly errors: string[];
  constructor(errors: string[]) {
    super(errors.join(" "));
    this.name = "ShowerPricingError";
    this.errors = errors;
  }
}

export interface ShowerCtx {
  /** Installatieglas-variant (DEUREN) voor een kleurnaam + glasdikte (mm). */
  doorGlassFor: (color: string, thicknessMm: number) => GlassVariant | undefined;
  /** Gehard-variant (veiligheidsglas, VASTE panelen) voor een kleurnaam + glasdikte (mm). */
  panelGlassFor: (color: string, thicknessMm: number) => GlassVariant | undefined;
  /**
   * Marge-factor over de GLASBEWERKINGEN (scharnier-uitsparingen, knopboring) — dezelfde
   * `catalogFactor` die de glasconfigurator gebruikt. Zonder dit gingen de bewerkingen tegen
   * inkoop mee terwijl het glas zelf al marge had. Ontbreekt hij, dan geen opslag (inkoop-context).
   */
  opsMarginFactor?: (isDoor: boolean) => number;
  /** Artikelprijs (verkoop, excl. btw, cents) per slug; null/undefined = onbekend artikel. */
  articlePriceCents: (slug: string) => number | null | undefined;
  /** Artikelnaam per slug (voor de regel-labels); optioneel. */
  articleName?: (slug: string) => string | undefined;
}

// --- Validatie ----------------------------------------------------------------------------------
export function validateShowerInput(input: ShowerInput): string[] {
  const errors: string[] = [];
  const layout = getShowerLayout(input.layout);
  if (!layout) return [`Onbekende opstelling: ${input.layout}.`];
  if (!SHOWER_FINISHES.some((f) => f.key === input.finish)) errors.push(`Onbekende beslagkleur: ${input.finish}.`);
  if (!(Number.isFinite(input.heightMm) && input.heightMm >= SHOWER_MIN_H_MM && input.heightMm <= SHOWER_MAX_H_MM)) {
    errors.push(`Hoogte moet tussen ${SHOWER_MIN_H_MM} en ${SHOWER_MAX_H_MM} mm liggen.`);
  }
  if (!Array.isArray(input.widthsMm) || input.widthsMm.length !== layout.panels.length) {
    errors.push(`Deze opstelling heeft ${layout.panels.length} glasdelen; er zijn ${input.widthsMm?.length ?? 0} breedtes opgegeven.`);
  } else {
    layout.panels.forEach((p, i) => {
      const w = input.widthsMm[i]!;
      if (!(Number.isFinite(w) && w >= p.minWmm && w <= p.maxWmm)) {
        errors.push(`${p.label}: breedte moet tussen ${p.minWmm} en ${p.maxWmm} mm liggen.`);
      }
    });
  }
  if (!(Number.isInteger(input.quantity) && input.quantity >= 1 && input.quantity <= 25)) {
    errors.push("Aantal moet tussen 1 en 25 liggen.");
  }
  if (input.glassThicknessMm != null && !(SHOWER_GLASS_THICKNESSES as readonly number[]).includes(input.glassThicknessMm)) {
    errors.push(`Glasdikte moet ${SHOWER_GLASS_THICKNESSES.join(" of ")} mm zijn.`);
  }
  return errors;
}

// --- Prijsberekening ----------------------------------------------------------------------------
/**
 * Prijst een complete douche-opstelling: glasdelen via de enkelglas-engine (8 mm gehard, incl.
 * scharnier-uitsparingen + knop-boring op de deur) + beslag tegen catalogus-verkoopprijzen.
 * @throws {ShowerPricingError} bij ongeldige invoer of een onbekend beslag-artikel.
 */
export function calculateShowerPrice(input: ShowerInput, ctx: ShowerCtx): ShowerPrice {
  const inputErrors = validateShowerInput(input);
  if (inputErrors.length > 0) throw new ShowerPricingError(inputErrors);
  const layout = getShowerLayout(input.layout)!;

  // Gedeelde uitstraling voor de hele cabine (legacy matGlass → "mat" als er geen key is).
  const app = showerAppearance(input.glassColor ?? (input.matGlass ? "mat" : undefined));
  // Glasdikte voor de hele cabine (deur + vaste panelen); default 8 mm.
  const thicknessMm = input.glassThicknessMm ?? SHOWER_DEFAULT_THICKNESS_MM;

  // Scharnier = een concreet catalogus-artikel (slug); de configurator toont álle passende
  //   scharnieren. Onbekende/lege keuze → de veilige default voor deze montage.
  const hingeMount = showerHingeMount(input.layout, input.hingeSide);
  const scharnierSlug = input.hardware?.scharnier && HINGE_LOADS[input.hardware.scharnier]
    ? input.hardware.scharnier
    : SHOWER_DEFAULT_HINGE[hingeMount];
  const scharnierPick: HardwarePick = { slug: scharnierSlug };
  const scharnierCap = showerHingeCapacity(scharnierSlug);

  // Aantal scharnieren per deur: 3 bij deuren >2 m (stabiliteit), anders 2. Het gekozen scharnier
  //   moet het gewicht + de breedte wél aankunnen (harde max, anders niet geschikt).
  const doorHinges = layout.panels.map((p, i) => {
    if (p.kind !== "deur") return 0;
    const w = input.widthsMm[i]!;
    return showerHingeFit(showerGlassWeightKg(w, input.heightMm, thicknessMm), w, input.heightMm, scharnierCap).count;
  });
  layout.panels.forEach((p, i) => {
    if (p.kind === "deur" && doorHinges[i] == null) {
      const w = input.widthsMm[i]!;
      const reason = showerHingeFit(showerGlassWeightKg(w, input.heightMm, thicknessMm), w, input.heightMm, scharnierCap).reason;
      throw new ShowerPricingError([reason === "breedte"
        ? `${p.label} is te breed voor dit scharnier (max. ${scharnierCap.maxDoorWidthMm} mm) — kies een ander scharnier.`
        : `${p.label} is te zwaar voor dit scharnier — kies een scharnier met meer draagvermogen of neem contact op.`]);
    }
  });

  // — Glasdelen: DEUR = installatieglas (+ scharnier-uitsparingen + knop-boring); VAST paneel =
  //   veiligheidsglas/gehard (kaal — het U-profiel + de Storm-stabilisatiestang klemmen).
  const glass: ShowerGlassLine[] = layout.panels.map((p, i) => {
    const widthMm = input.widthsMm[i]!;
    const isDoor = p.kind === "deur";
    const variant =
      (isDoor ? ctx.doorGlassFor(app.doorColor, thicknessMm) : ctx.panelGlassFor(app.panelColor, thicknessMm)) ??
      // Terugval op de standaarduitstraling. Stond hier als de kale tekst "Helder"; dat brak toen
      // het paneelglas naar de API-naamgeving ging ("Gehard blank"). De kleurnaam hoort uit
      // SHOWER_GLASS_APPEARANCES te komen, want daar staat per glassoort hoe hij heet.
      (isDoor ? ctx.doorGlassFor(STANDAARD_UITSTRALING.doorColor, thicknessMm) : ctx.panelGlassFor(STANDAARD_UITSTRALING.panelColor, thicknessMm));
    if (!variant) {
      throw new ShowerPricingError([`${p.label}: ${isDoor ? "installatieglas" : "veiligheidsglas"} "${app.label}" (${thicknessMm} mm) is momenteel niet beschikbaar.`]);
    }
    // Herschaal de glasprijs naar de douche-dealerkorting (SHOWER_DOORS_PANELS, 55%): de
    //   variant-prijs heeft de soort-korting al ingebakken (deur 55%, gehard paneel 57%).
    //   Sinds 02-08-2026 staat die herschaalde prijs vooraf uitgerekend op de variant
    //   (showerPricePerM2, build-glass-manual-public.mjs) zodat de korting zelf niet meer in de
    //   publieke bundel hoeft; de formule blijft als terugval voor een server-catalogus mét korting.
    const soortDisc = variant.timmersDiscountPct ?? SHOWER_GLASS_DISCOUNT_PCT;
    const pricedVariant =
      variant.showerPricePerM2 != null
        ? variant.showerPricePerM2 === variant.pricePerM2
          ? variant
          : { ...variant, pricePerM2: variant.showerPricePerM2 }
        : soortDisc === SHOWER_GLASS_DISCOUNT_PCT
          ? variant
          : { ...variant, pricePerM2: Math.round((variant.pricePerM2 * (1 - SHOWER_GLASS_DISCOUNT_PCT / 100)) / (1 - soortDisc / 100)), timmersDiscountPct: SHOWER_GLASS_DISCOUNT_PCT };
    const processings = isDoor
      ? [
          { kind: "uitsparing" as const, sizeMm: HINGE_CUTOUT_MM, count: doorHinges[i]! },
          { kind: "boring" as const, sizeMm: KNOB_BORE_MM, count: 1 },
        ]
      : undefined;
    let price;
    try {
      price = calculateGlassPrice(
        { variantId: pricedVariant.id, measureType: "glas", widthMm, heightMm: input.heightMm, quantity: 1, processings },
        { variant: pricedVariant, opsMarginFactor: ctx.opsMarginFactor?.(isDoor) },
      );
    } catch (e) {
      if (e instanceof PricingError) throw new ShowerPricingError(e.errors.map((m) => `${p.label}: ${m}`));
      throw e;
    }
    return {
      panel: p.key,
      label: `${p.label} ${widthMm} × ${input.heightMm} mm · ${thicknessMm} mm ${app.label} · ${isDoor ? "installatieglas" : "veiligheidsglas"}`,
      widthMm,
      heightMm: input.heightMm,
      cents: price.subtotalExcl,
    };
  });

  // — Beslag volgens het opstelling-recept + de eigen samenstelling (hardware-keuzes):
  //   per deur 2 scharnieren + knop + onderprofiel; per vast glasdeel een muurprofiel +
  //   stabilisatiestang; haakse hoek → hoekprofiel.
  const hw = input.hardware ?? {};
  const fin = input.finish;
  const picks: { role: HardwareRole; pick: HardwarePick | undefined; count: number }[] = [];
  const deuren = layout.panels.filter((p) => p.kind === "deur").length;
  const vastePanelen = layout.panels.length - deuren;
  // Elke beslagrol (knop/stang/profiel/afdichting) kan een concreet catalogus-artikel (slug) zijn:
  //   de configurator toont álle passende artikelen. Een lege/legacy keuze (optie-id) valt terug op
  //   de finish-gematchte standaard-optie.
  const asSlugPick = (role: ShowerHwRole, value?: string): HardwarePick | undefined =>
    value && !SHOWER_HW_OPTIONS[role].some((o) => o.id === value) ? { slug: value } : undefined;
  if (deuren > 0) {
    const totalHinges = doorHinges.reduce((s: number, h) => s + (h ?? 0), 0);
    picks.push({ role: "scharnier", pick: scharnierPick, count: totalHinges });
    picks.push({ role: "knop", pick: asSlugPick("knop", hw.knop) ?? pickForFinish(showerHwOption("knop", hw.knop).picks, fin), count: deuren });
    // Afdichting onder de deur is optioneel (je kunt ook afkitten) — alleen bij expliciet aanzetten.
    if (input.sealDoorOn) picks.push({ role: "afdichting-onder", pick: asSlugPick("sealDoor", hw.sealDoor) ?? pickForFinish(showerHwOption("sealDoor", hw.sealDoor).picks, fin), count: deuren });
  }
  if (vastePanelen > 0) {
    // Stabilisatiestang = de stang zelf + een glasaansluiting + een wandaansluiting (per vast paneel).
    picks.push({ role: "stabilisatiestang", pick: asSlugPick("stang", hw.stang) ?? pickForFinish(showerHwOption("stang", hw.stang).picks, fin), count: vastePanelen });
    picks.push({ role: "stang-glasaansluiting", pick: asSlugPick("stangGlas", hw.stangGlas) ?? pickForFinish(showerHwOption("stangGlas", hw.stangGlas).picks, fin), count: vastePanelen });
    picks.push({ role: "stang-wandaansluiting", pick: asSlugPick("stangWand", hw.stangWand) ?? pickForFinish(showerHwOption("stangWand", hw.stangWand).picks, fin), count: vastePanelen });
    // Klemmen of profiel — het een óf het ander. Twee klemmen per vast paneel.
    if (input.paneelBevestiging === "klemmen") {
      picks.push({ role: "wandklem", pick: asSlugPick("klem", hw.klem) ?? pickForFinish(showerHwOption("klem", hw.klem).picks, fin), count: vastePanelen * SHOWER_KLEMMEN_PER_PANEEL });
    } else {
      picks.push({ role: "wandprofiel", pick: asSlugPick("uProfiel", hw.uProfiel) ?? pickForFinish(showerHwOption("uProfiel", hw.uProfiel).picks, fin), count: vastePanelen });
    }
  }
  if (layout.corner && input.sealHoekOn) {
    // Optioneel hoekafdichtprofiel; zonder expliciete keuze volgt het de beslagkleur (zwart ↔ transp.).
    const hoekId = hw.sealHoek ?? (fin === "zwart" ? "zwart" : "transparant");
    picks.push({ role: "afdichting-hoek", pick: asSlugPick("sealHoek", hw.sealHoek) ?? pickForFinish(showerHwOption("sealHoek", hoekId).picks, fin), count: 1 });
  }

  const hwErrors: string[] = [];
  const hardware: ShowerHardwareLine[] = [];
  for (const { role, pick, count } of picks) {
    if (!pick) { hwErrors.push(`Beslag voor ${role} niet beschikbaar in deze uitvoering.`); continue; }
    const unit = ctx.articlePriceCents(pick.slug);
    if (unit == null || !(unit > 0)) {
      hwErrors.push(`Beslag-artikel niet gevonden in de catalogus: ${pick.slug}.`);
      continue;
    }
    hardware.push({
      role,
      slug: pick.slug,
      label: ctx.articleName?.(pick.slug) ?? pick.slug,
      count,
      unitCents: unit,
      totalCents: unit * count,
      ...(pick.fallback ? { finishFallback: true } : {}),
    });
  }
  if (hwErrors.length > 0) throw new ShowerPricingError(hwErrors);

  const glassCents = glass.reduce((s, g) => s + g.cents, 0);
  const hardwareCents = hardware.reduce((s, h) => s + h.totalCents, 0);
  const unitCents = glassCents + hardwareCents;
  return {
    layout,
    finish: input.finish,
    glass,
    hardware,
    glassCents,
    hardwareCents,
    unitCents,
    totalCents: unitCents * input.quantity,
  };
}
